import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { useSelector } from 'react-redux';
import { Cookies } from '../../../index.js';
import Language_i18 from '../../../js/useNationalLanguage.jsx';

function OrdersMetaPill({ icon, children, className = "" }) {
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] ${className}`}>
            {icon}
            {children}
        </span>
    );
}

const OrdersHomeAi = () => {
    const { lang } = Language_i18();
    const latest_version = useSelector(state => state.latest_version);
    const [digitalSuiteApiData, setDigitalSuiteApiData] = useState([]);
    const [isLoadingDigitalSuite, setIsLoadingDigitalSuite] = useState(true);
    const showWorkflowAutomation = false; // Temporary hide without removing code
    const useNextOrdersOnce = latest_version == 3 || latest_version == 5;
    const ordersOnceBase = useNextOrdersOnce ? "/next/orders_once" : "/ai/orders_once";
    const aiCardImages = {
        customDev: "https://hust.media/img/workflow_automation_thumbnail.png",
    };
    const copyByLang = {
        en: {
            title: "Practical AI Utilities for Content Workflows",
            description: "Practical AI utilities for speech, text, and image workflows, built on stable system integration and real infrastructure.",
            keywords: "ai utilities, content workflows, text to speech, speech to text, image to text, OCR, translation, workflow automation, system integration",
            heading: "Practical Utilities Built on System Integration",
            summary: "This section brings together a small set of practical AI utilities built from the same foundation of architecture, integration, and stable operation used across the platform. It covers speech, text, image, and translation workflows, showing how real infrastructure, applied system design, and controlled processing flow support practical content and media tasks in a more reliable and maintainable way.",
            cards: {
                // --- CARD 1: PROFESSIONAL CODING SERVICE (PRIORITY) ---
                customDev: {
                    title: "Workflow Automation Utilities",
                    body: "Practical utilities for automating repeatable tasks across content, operations, and connected workflows.",
                    use: "Use cases: process automation, workflow support, and system integration.",
                    link: "/next/services/development" // Link to service page
                },
                tts: {
                    use: "Use cases: voice-over, reading long notes, assisting users with visual impairment.",
                },
                stt: {
                    use: "Use cases: meeting notes, interviews, voice input for mobile users.",
                },
                ocr: {
                    use: "Use cases: screenshots, receipts, documents, proof-of-work submissions.",
                },
                translate_vi_en: {
                    use: "Use cases: blog drafts, product descriptions, support replies, and internal notes.",
                },
            },
            privacyTitle: "Responsible Use & Privacy",
            privacyBody: "Please avoid uploading sensitive personal information when using these tools. To generate results, some media may be processed on the server side. These tools are intended for legitimate productivity, accessibility, transcription, and verification tasks.",
        },
        vi: {
            title: "Practical AI Utilities for Content Workflows",
            description: "Practical AI utilities for speech, text, and image workflows, built on stable system integration and real infrastructure.",
            keywords: "ai utilities, content workflows, text to speech, speech to text, image to text, OCR, translation, workflow automation, system integration",
            heading: "Giải pháp Công nghệ & Tiện ích số",
            summary: "This section brings together a small set of practical AI utilities built from the same foundation of architecture, integration, and stable operation used across the platform. It covers speech, text, image, and translation workflows, showing how real infrastructure, applied system design, and controlled processing flow support practical content and media tasks in a more reliable and maintainable way.",
            cards: {
                // --- CARD 1: NHẬN CODE THUÊ (ƯU TIÊN HIỂN THỊ) ---
                customDev: {
                    title: "Workflow Automation Utilities",
                    body: "Practical utilities for automating repeatable tasks across content, operations, and connected workflows.",
                    use: "Use cases: process automation, workflow support, and system integration.",
                    link: "/next/services/development" // Link tới trang dịch vụ
                },
                tts: {
                    use: "Tình huống: lồng tiếng, đọc ghi chú dài, hỗ trợ người khiếm thị.",
                },
                stt: {
                    use: "Tình huống: ghi chú họp, phỏng vấn, nhập liệu bằng giọng nói.",
                },
                ocr: {
                    use: "Tình huống: ảnh chụp màn hình, hóa đơn, tài liệu, minh chứng công việc.",
                },
                translate_vi_en: {
                    use: "Use cases: blog drafts, product descriptions, support replies, and internal notes.",
                },
            },
            privacyTitle: "Sử dụng có trách nhiệm & quyền riêng tư",
            privacyBody: "Tránh tải lên dữ liệu cá nhân nhạy cảm. Nội dung có thể được xử lý phía máy chủ để tạo kết quả. Hãy dùng cho mục đích hợp pháp: năng suất, trợ năng và kiểm chứng.",
        },
    };
    const copy = copyByLang?.[lang] || copyByLang.en;

    useEffect(() => {
        let isMounted = true;
        const fetchDigitalSuite = async () => {
            try {
                const response = await fetch("https://hust.media/api/content/getdata_v2.php?uri=digital_suite&mode=category");
                const payload = await response.json();
                if (!isMounted) return;
                if (payload?.status === 1 && Array.isArray(payload?.data)) {
                    setDigitalSuiteApiData(payload.data);
                }
            } catch (error) {
                console.warn("Failed to load digital_suite category:", error);
            } finally {
                if (isMounted) setIsLoadingDigitalSuite(false);
            }
        };
        fetchDigitalSuite();
        return () => {
            isMounted = false;
        };
    }, []);

    const utilityUseByUri = useMemo(() => ({
        text_speech: String(copy?.cards?.tts?.use || "").trim(),
        speech_text: String(copy?.cards?.stt?.use || "").trim(),
        image_text: String(copy?.cards?.ocr?.use || "").trim(),
        translate_vi_en: String(copy?.cards?.translate_vi_en?.use || "").trim(),
    }), [copy]);

    const formatUpdated = (value) => {
        const raw = String(value || "").trim();
        if (!raw) return "";
        const date = new Date(raw.replace(" ", "T"));
        if (Number.isNaN(date.getTime())) return raw;
        return date.toLocaleDateString("en-US");
    };

    const utilityCards = useMemo(() => {
        if (!Array.isArray(digitalSuiteApiData)) return [];
        return digitalSuiteApiData
            .map((item) => {
                const uri = String(item?.uri || "").trim();
                if (!uri) return null;
                return {
                    key: String(item?.id || uri),
                    href: `${ordersOnceBase}/${uri}`,
                    title: String(item?.title || "").trim(),
                    body: String(item?.description || "").trim(),
                    image: String(item?.thumbnail_image || item?.image || "").trim(),
                    updated: formatUpdated(item?.createdate),
                    category: String(item?.tips_hash_name || "").trim(),
                    use: utilityUseByUri[uri] || "",
                };
            })
            .filter(Boolean);
    }, [digitalSuiteApiData, ordersOnceBase, utilityUseByUri]);

    return (
        <>
            <Helmet>
                <title>{copy.title}</title>
                <meta name="description" content={copy.description} />
                <meta name="keywords" content={copy.keywords} />
            </Helmet>

        <div className="max-w-6xl mx-auto mb-7">
            <div className="mt-6 mx-1 grid grid-cols-1">
                <div className="bg-green-200 bg-opacity-70 p-3.5 rounded-xl shadow-md md:col-span-2">
                    <div className="px-2.5 mt-2 mb-3">
                        <h1 className="text-[22px] font-semibold text-slate-900">
                            {copy.heading}
                        </h1>
                        <p className="text-[14.5px] text-slate-700 mt-1">
                            {copy.summary}
                        </p>
                    </div>

                    {/* GRID LAYOUT: max 3 items per row (item 4 wraps) */}
                    {isLoadingDigitalSuite ? (
                        <div className="flex min-h-[180px] items-center justify-center">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
                                <span>{lang === "vi" ? "Đang tải dữ liệu..." : "Loading data..."}</span>
                            </div>
                        </div>
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        
                        {/* --- CARD 1: CUSTOM DEV (MOVED TO FIRST POSITION) --- */}
                        {showWorkflowAutomation && (
                            <a
                                className="block rounded-2xl bg-indigo-50/90 backdrop-blur-md border border-indigo-200 shadow-sm hover:shadow-md transition p-3.5 no-underline relative group ring-1 ring-indigo-200"
                                href={copy.cards.customDev.link}
                            >
                                {/* Hot Badge */}
                                <div className="absolute top-2 right-2 w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                                
                                <div className="text-lg font-bold text-indigo-900 group-hover:text-indigo-700 transition-colors">
                                    {copy.cards.customDev.title}
                                </div>
                                <div className="mt-1.5 mb-1.5 mx-auto w-[85%] rounded-xl border border-indigo-200/70 bg-white/70 p-1.5">
                                    <img
                                        src={aiCardImages.customDev}
                                        alt={copy.cards.customDev.title}
                                        loading="lazy"
                                        className="mx-auto h-auto max-h-28 w-full object-contain"
                                    />
                                </div>
                                <div className="mt-1 text-[14.5px] leading-6 text-slate-700">
                                    {copy.cards.customDev.body}
                                </div>
                                <div className="mt-2.5 text-[13px] leading-5 text-slate-600">
                                    {copy.cards.customDev.use}
                                </div>
                            </a>
                        )}

                        {utilityCards.map((card) => (
                            <a
                                key={card.key}
                                className="block rounded-2xl bg-white/60 backdrop-blur-md border border-white/60 shadow-sm hover:shadow-md transition p-3.5 no-underline"
                                href={card.href}
                            >
                                <div className="text-lg font-semibold text-slate-900">{card.title}</div>
                                {card.image ? (
                                    <div className="mt-1.5 mb-1.5 mx-auto w-[85%] rounded-xl border border-slate-200/70 bg-white/70 p-1.5">
                                        <img
                                            src={card.image}
                                            alt={card.title}
                                            loading="lazy"
                                            className="mx-auto h-auto max-h-28 w-full object-contain"
                                        />
                                    </div>
                                ) : null}
                                <div className="mt-1 text-[15px] leading-6 text-slate-700">
                                    {card.body}
                                </div>
                                {(card.updated || card.category) ? (
                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                        {card.updated ? (
                                            <OrdersMetaPill
                                                className="border border-slate-300/80 bg-slate-100/75 text-slate-600"
                                                icon={
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        aria-hidden="true"
                                                        className="mr-1 shrink-0"
                                                    >
                                                        <path
                                                            d="M7.5 2.75v2.5M16.5 2.75v2.5M3.75 8.75h16.5M6 4.75h12A2.25 2.25 0 0 1 20.25 7v11A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V7A2.25 2.25 0 0 1 6 4.75Z"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                }
                                            >
                                                {card.updated}
                                            </OrdersMetaPill>
                                        ) : null}
                                        {card.category ? (
                                            <OrdersMetaPill
                                                className="border border-slate-300/70 bg-white/45 font-medium text-slate-700"
                                                icon={
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        aria-hidden="true"
                                                        className="mr-1 shrink-0"
                                                    >
                                                        <path
                                                            d="M10.25 4.75H6.75A2.25 2.25 0 0 0 4.5 7v4.043a2.25 2.25 0 0 0 .659 1.591l5.707 5.707a2.25 2.25 0 0 0 3.182 0l4.293-4.293a2.25 2.25 0 0 0 0-3.182l-5.909-5.909a2.25 2.25 0 0 0-1.591-.659Z"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                        <circle cx="8.25" cy="8.25" r="1.1" fill="currentColor" />
                                                    </svg>
                                                }
                                            >
                                                {card.category}
                                            </OrdersMetaPill>
                                        ) : null}
                                    </div>
                                ) : null}
                                {card.use ? (
                                    <div className="mt-2.5 text-[13px] leading-5 text-slate-600">
                                        {card.use}
                                    </div>
                                ) : null}
                            </a>
                        ))}

                    </div>
                    )}

                    <div className="mt-3 py-3 rounded-xl bg-white/40 border border-white/50 p-2.5">
                        <div className="text-base font-semibold text-slate-900">{copy.privacyTitle}</div>
                        <div className="text-sm text-slate-700 mt-1">
                            {copy.privacyBody}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default OrdersHomeAi;
